import { WIDGET_FILTER_TYPES, WIDGET_FILTER_TYPES_KEYS } from 'common/Widgets/constants';
import { isViewCMA } from 'components/CMASummary/utils';

import {
  AGGREGATION_TYPE,
  DEFAULT_FILTERS,
  VALUE_DIFFERENCE_DISPLAY,
  VIEW_CATEGORY,
  VIEW_FILTER_KEYS,
} from './constants';
import { areDataQuarterSelectionsEqual, createDefaultDataQuarterSelection } from './filters/DataQuarterFilter/helpers';
import { SnapshotAge, ViewFilters } from './types';
import { COMMUNITY_CONTRACT_SELECTION_TYPE } from '../../constants';
import { CommunityContract } from '../../types/contracts';

const VALUE_DISPLAY_DEFAULT = 0;

export const transformFilterInbound = (baseFilter: any) => {
  if (!baseFilter) return DEFAULT_FILTERS;

  const {
    id,
    competitors,
    communities,
    care_types,
    room_types,
    room_types_price_display,
    care_levels,
    medication_levels,
    review_sources,
    review_categories,
    swot_categories,
    amenity_categories,
    contract_types,
    community_contract_type,
    community_contract_selection_type,
    community_contract_slugs,
    care_levels_stats_type,
    medication_levels_stats_type,
    price_display,
    selected_quarter_slug,
    include_prior_competitors,
    comparison_type_enabled,
    value_display,
  } = baseFilter;

  return {
    id: id,
    competitors: competitors,
    communities: communities,
    careTypes: care_types,
    roomTypes: room_types,
    roomTypesPriceDisplay: room_types_price_display || AGGREGATION_TYPE.AVG,
    careLevels: care_levels,
    medicationLevels: medication_levels,
    reviewSources: review_sources,
    reviewCategories: review_categories,
    swotCategories: swot_categories,
    amenityCategories: amenity_categories,
    contractTypes: contract_types ?? [],
    communityContractType: community_contract_type,
    communityContractSelectionType: community_contract_selection_type ?? COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE,
    communityContractSlugs: community_contract_slugs ?? [],
    careLevelsStatsType: care_levels_stats_type || AGGREGATION_TYPE.AVG,
    medicationLevelsStatsType: medication_levels_stats_type || AGGREGATION_TYPE.AVG,
    priceDisplay: price_display,
    dataQuarterSelection: {
      ...createDefaultDataQuarterSelection(),
      selectedQuarterSlug: selected_quarter_slug ?? null,
      includePriorCompetitors: include_prior_competitors ?? true,
    },
    comparisonTypeEnabled: comparison_type_enabled ?? true,
    valueDisplay:
      (comparison_type_enabled ?? true)
        ? value_display === VALUE_DIFFERENCE_DISPLAY.PERCENTAGES
          ? VALUE_DIFFERENCE_DISPLAY.PERCENTAGES
          : VALUE_DIFFERENCE_DISPLAY.AMOUNTS
        : (value_display ?? VALUE_DIFFERENCE_DISPLAY.DONT_DISPLAY),
  };
};

export const transformFilterOutbound = (baseFilter: any) => {
  const {
    competitors,
    communities,
    careTypes,
    roomTypes,
    roomTypesPriceDisplay,
    careLevels,
    medicationLevels,
    reviewSources,
    reviewCategories,
    swotCategories,
    careLevelsStatsType,
    medicationLevelsStatsType,
    dataQuarterSelection,
    comparisonTypeEnabled,
    valueDisplay,
    contractTypes,
    communityContractType,
    communityContractSelectionType,
    communityContractSlugs,
  } = baseFilter;

  return {
    competitors: competitors,
    communities: communities,
    care_types: careTypes,
    room_types: roomTypes,
    room_types_price_display: roomTypesPriceDisplay,
    care_levels: careLevels,
    medication_levels: medicationLevels,
    review_sources: reviewSources,
    review_categories: reviewCategories,
    swot_categories: swotCategories,
    care_levels_stats_type: careLevelsStatsType,
    medication_levels_stats_type: medicationLevelsStatsType,
    selected_quarter_slug: dataQuarterSelection?.selectedQuarterSlug ?? null,
    include_prior_competitors: dataQuarterSelection?.includePriorCompetitors ?? true,
    comparison_type_enabled: comparisonTypeEnabled ?? true,
    value_display: valueDisplay ?? VALUE_DISPLAY_DEFAULT,
    contract_types: contractTypes,
    community_contract_type: communityContractType,
    community_contract_selection_type: communityContractSelectionType,
    community_contract_slugs: communityContractSlugs,
  };
};

export const equalFilters = (initialFilter: any, currentFilter: any) => {
  if (!initialFilter || !currentFilter) return false;

  const {
    care_types,
    room_types,
    room_types_price_display,
    care_levels,
    medication_levels,
    review_sources,
    review_categories,
    swot_categories,
    care_levels_stats_type,
    medication_levels_stats_type,
    selected_quarter_slug,
    include_prior_competitors,
    comparison_type_enabled,
    value_display,
    contract_types,
    community_contract_type,
    community_contract_selection_type,
    community_contract_slugs,
  } = initialFilter;
  const {
    careTypes,
    roomTypes,
    roomTypesPriceDisplay,
    careLevels,
    medicationLevels,
    reviewSources,
    reviewCategories,
    swotCategories,
    careLevelsStatsType,
    medicationLevelsStatsType,
    dataQuarterSelection,
    comparisonTypeEnabled,
    valueDisplay,
  } = currentFilter;
  const initialQuarterSelection = {
    ...createDefaultDataQuarterSelection(),
    selectedQuarterSlug: selected_quarter_slug ?? null,
    includePriorCompetitors: include_prior_competitors ?? true,
  };
  const currentQuarterSelection = {
    ...createDefaultDataQuarterSelection(),
    ...(dataQuarterSelection ?? {}),
  };
  const initialComparisonType = comparison_type_enabled ?? true;
  const currentComparisonType = comparisonTypeEnabled ?? false;
  const initialValueDisplay = value_display ?? VALUE_DISPLAY_DEFAULT;
  const currentValueDisplay = valueDisplay ?? VALUE_DISPLAY_DEFAULT;

  return (
    initialFilter.competitors == currentFilter.competitors &&
    care_types == careTypes &&
    room_types == roomTypes &&
    room_types_price_display == roomTypesPriceDisplay &&
    care_levels == careLevels &&
    medication_levels == medicationLevels &&
    review_sources == reviewSources &&
    review_categories == reviewCategories &&
    swot_categories == swotCategories &&
    care_levels_stats_type == careLevelsStatsType &&
    medication_levels_stats_type == medicationLevelsStatsType &&
    initialComparisonType == currentComparisonType &&
    initialValueDisplay == currentValueDisplay &&
    areDataQuarterSelectionsEqual(initialQuarterSelection, currentQuarterSelection) &&
    contract_types == currentFilter.contractTypes &&
    community_contract_type == currentFilter.communityContractType &&
    community_contract_selection_type == currentFilter.communityContractSelectionType &&
    community_contract_slugs == currentFilter.communityContractSlugs
  );
};

const applyFallbackContract = (
  source: any,
  fallbackContract: CommunityContract | undefined,
  fallbackSelectionType: number
) => {
  if (!fallbackContract) {
    return source;
  }

  const hasSlug = source?.communityContractSlugs && source.communityContractSlugs.length > 0;
  const hasId = Boolean(source?.communityContractType);

  if (hasId || hasSlug) {
    return source;
  }

  const existingContractTypes = source?.contractTypes;

  return {
    ...source,
    communityContractType: fallbackContract.id,
    communityContractSelectionType: source?.communityContractSelectionType ?? fallbackSelectionType,
    contractTypes:
      existingContractTypes && existingContractTypes.length > 0
        ? existingContractTypes
        : [fallbackContract.contract_id],
  };
};

const ensureContractBelongsToCommunity = (
  source: any,
  communityContracts: CommunityContract[] | undefined,
  fallbackSelectionType: number
) => {
  if (!communityContracts?.length) {
    return source;
  }

  if (!source?.communityContractType) {
    return source;
  }

  const exists = communityContracts.some(
    (communityContract: CommunityContract) => communityContract.id === source.communityContractType
  );

  if (exists) {
    return source;
  }

  const fallback = communityContracts[0];

  const existingContractTypes = source?.contractTypes;

  return {
    ...source,
    communityContractType: fallback.id,
    communityContractSelectionType: source?.communityContractSelectionType ?? fallbackSelectionType,
    contractTypes:
      existingContractTypes && existingContractTypes.length > 0 ? existingContractTypes : [fallback.contract_id],
  };
};

export const getWidgetFilter = (
  isDefaultView: boolean,
  globalFilters: any,
  itemFilters: any,
  communityContracts?: CommunityContract[],
  fallbackContract?: CommunityContract,
  fallbackSelectionType: number = COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE
) => {
  if (isDefaultView) {
    const baseFilters = globalFilters ? { ...globalFilters } : {};
    return ensureContractBelongsToCommunity(
      applyFallbackContract(baseFilters, fallbackContract, fallbackSelectionType),
      communityContracts,
      fallbackSelectionType
    );
  }

  const filters = transformFilterInbound(itemFilters);

  let results: any = Object.assign({}, filters, {
    competitors: globalFilters?.competitors ?? filters.competitors ?? [],
    dataQuarterSelection: {
      selectedQuarterSlug:
        globalFilters?.dataQuarterSelection?.selectedQuarterSlug ??
        filters?.dataQuarterSelection?.selectedQuarterSlug ??
        null,
      includePriorCompetitors:
        globalFilters?.dataQuarterSelection?.includePriorCompetitors ??
        filters?.dataQuarterSelection?.includePriorCompetitors ??
        true,
    },
    comparisonTypeEnabled: globalFilters?.comparisonTypeEnabled ?? filters?.comparisonTypeEnabled ?? true,
    valueDisplay: globalFilters?.valueDisplay ?? filters?.valueDisplay ?? VALUE_DIFFERENCE_DISPLAY.AMOUNTS,
  });

  if (communityContracts?.length) {
    const communityContractPresentInCommunity = communityContracts.find(
      (communityContract: CommunityContract) => results.communityContractType === communityContract.id
    );
    if (!communityContractPresentInCommunity) {
      results = Object.assign({}, results, { communityContractType: communityContracts[0].id });
    }
  }

  return ensureContractBelongsToCommunity(
    applyFallbackContract(results, fallbackContract, fallbackSelectionType),
    communityContracts,
    fallbackSelectionType
  );
};

export const isViewSalesBattlecard = (view?: { category?: string }) => {
  return view?.category === VIEW_CATEGORY.SALES_BATTLECARD;
};

export const sortViews = (a: any, b: any) => {
  /**
   * sort views order:
   * 1. Default views
   * 2. Sales Battlecard
   * 3. CMA Summary
   * 4. Rest of the views (custom views)
   */
  if (a.default && !b.default) {
    return -1;
  }
  if (!a.default && b.default) {
    return 1;
  }
  const aSalesBattlecard = isViewSalesBattlecard(a) ? 1 : 0;
  const bSalesBattlecard = isViewSalesBattlecard(b) ? 1 : 0;
  if (aSalesBattlecard !== bSalesBattlecard) {
    return bSalesBattlecard - aSalesBattlecard;
  }
  return Number(isViewCMA(b)) - Number(isViewCMA(a));
};

export const getFiltersFromUrl = () => {
  const params = new URLSearchParams(window.location.search);

  const filters = Object.values(WIDGET_FILTER_TYPES_KEYS).reduce((acc: any, key) => {
    if (
      key === WIDGET_FILTER_TYPES_KEYS[WIDGET_FILTER_TYPES.CARE_LEVELS_STATS_TYPE] ||
      key === WIDGET_FILTER_TYPES_KEYS[WIDGET_FILTER_TYPES.MEDICATION_LEVELS_STATS_TYPE]
    ) {
      acc[key] = params.get(key) ? Number(params.get(key)) : null;
    } else {
      acc[key] = params.getAll(key).map(Number);
    }
    return acc;
  }, {});

  const hasFilters = Object.entries(filters).some(([_, value]) =>
    Array.isArray(value) ? value.length > 0 : value !== null
  );

  return hasFilters ? filters : null;
};

export const convertViewFiltersToQueryString = (filters: ViewFilters): string => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (key === VIEW_FILTER_KEYS.DATA_QUARTER_SELECTION) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, v.toString()));
    } else if (value !== null && typeof value !== 'object') {
      params.append(key, value.toString());
    }
  });

  return `?${params.toString()}`;
};

export const getOrdinalSuffix = (n: number) => {
  const suffix = ['th', 'st', 'nd', 'rd'];
  const exceptions = [11, 12, 13];

  const remainder = n % 10;
  const exception = exceptions.includes(n % 100);

  if (exception) {
    return `${n}th`;
  }

  return `${n}${suffix[remainder < 4 ? remainder : 0]}`;
};

const getSnapshotAge = (latestSnapshotDate?: string | null): SnapshotAge => {
  if (!latestSnapshotDate) {
    return { exists: false };
  }

  const snapshotDate = new Date(latestSnapshotDate);
  const now = new Date();

  const totalMonths =
    (now.getFullYear() - snapshotDate.getFullYear()) * 12 + (now.getMonth() - snapshotDate.getMonth());

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  return {
    exists: true,
    totalMonths,
    years,
    months,
  };
};

export const getSnapshotReminder = (latestSnapshotDate?: string | null) => {
  const info = getSnapshotAge(latestSnapshotDate);

  if (!info.exists) {
    return {
      type: 'warning' as const,
      message: 'Community data has never been updated.',
    };
  }

  const { totalMonths, years, months } = info;

  if (totalMonths < 9) {
    return null;
  }

  if (totalMonths < 12) {
    return {
      type: 'warning' as const,
      message: `Community data was updated over ${totalMonths} months ago.`,
    };
  }

  const yearText = years ? `${years} year${years > 1 ? 's' : ''}` : '';
  const monthText = months ? `${months} month${months > 1 ? 's' : ''}` : '';

  return {
    type: 'warning' as const,
    message: `Critical: Community data was updated over ${[yearText, monthText].filter(Boolean).join(' and ')} ago.`,
  };
};
